/**
 * Book Away — Google Analytics 4 tracking helpers.
 *
 * `gtag` is loaded via a tag injected in `index.html` (see `initAnalytics()`
 * below). All calls are safe no-ops when the tag isn't loaded (e.g. local
 * dev without `REACT_APP_GA4_ID`, or CI, or ad-blocker) so instrumenting
 * new interactions never risks a runtime error.
 */

const GA4_ID = process.env.REACT_APP_GA4_ID || "";

/**
 * Inject the Google tag once at app boot. Safe to call multiple times —
 * the second call short-circuits. No-op when `REACT_APP_GA4_ID` is unset.
 */
export function initAnalytics() {
  if (typeof window === "undefined") return;
  if (!GA4_ID) return;
  if (window.__ba_ga4_loaded) return;
  window.__ba_ga4_loaded = true;

  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`;
  document.head.appendChild(s);

  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line prefer-rest-params
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;
  gtag("js", new Date());
  gtag("config", GA4_ID, { send_page_view: true });
}

/**
 * Fire a GA4 event. Silently no-ops when the tag isn't loaded.
 *
 * @param {string} event Event name (snake_case is idiomatic in GA4).
 * @param {Record<string, unknown>} [params] Optional custom params.
 */
export function track(event, params = {}) {
  if (typeof window === "undefined") return;
  if (typeof window.gtag !== "function") return;
  try {
    window.gtag("event", event, params);
  } catch {
    // Never let analytics break a UX interaction.
  }
}

// ---- Named events used across the landing page ---------------------------
// Keep event names centralised so the marketing team can trace them back to
// the code that fires them without grepping.

export function trackCategoryClick(category) {
  track("category_click", { category });
}

export function trackMapStateHover(state) {
  track("map_state_hover", { state });
}

export function trackMapStateClick(state, city) {
  track("map_state_click", { state, city });
}

export function trackWaitlistOpen(city, source) {
  track("waitlist_open", { city, source });
}

export function trackWaitlistSubmit(city, category) {
  // Both the semantic "waitlist_submit" event AND the built-in "sign_up"
  // conversion — sign_up is a recommended GA4 event and shows up in the
  // Conversions view without extra configuration.
  track("waitlist_submit", { city, category });
  track("sign_up", { method: "waitlist", city, category });
}

/**
 * Fires when a returning business owner taps the "Log in" link on
 * /business/apply — lets the marketing team split returning-business
 * traffic from net-new sign-ups.
 */
export function trackBusinessLoginClick() {
  track("business_login_click", { surface: "business_apply" });
}
